/* eslint-disable no-invalid-this */
import Backbone from "backbone";
import Handlebars from "handlebars";
import _ from "underscore";

import {staticUrl} from "@repo/urls";

import Badges from "#badges-package/badges.ts";
import $ from "#jquery/jquery.ts";

import getBadgeTemplate from "./badge-compact.handlebars.ts";
import getBadgeDisplayCaseTemplate from "./badge-display-case.handlebars.ts";
import getEmptyBadgePickerTemplate from "./empty-badge-picker.handlebars.ts";

/**
 * A UI component that displays a list of badges to show off.
 * Typically used in a public profile page, but can be re-used
 * in the context of a hovercard, or any other context.
 *
 * Expects a Badges.BadgeList model to back it.
 */
const BadgeDisplayCase: any = Backbone.View.extend({
    className: "badge-display-case",

    /**
     * Whether or not this is currently in edit mode.
     */
    editing: false,

    /**
     * The full user badge list available to pick from when in edit mode.
     * @type {Badges.UserBadgeList}
     */
    fullBadgeList: null,

    /**
     * The number of slots available in the display case.
     */
    maxVisible: 5,

    /**
     * The slot number being edited. Any selection from the badge picker
     * will replace the badge in this slot number.
     * -1 if not currently editing.
     */
    selectedIndex: -1,

    mainCaseEl: null,
    badgePickerEl: null,
    editControlEl: null,

    /**
     * Ephemeral element used in animating a selection.
     */
    animatingBadgeEl: null,

    initialize: function (this: any) {
        this.model.bind("add", this.render, this);
        this.model.bind("remove", this.render, this);
        this.model.bind("change", this.render, this);
        this.template = getBadgeDisplayCaseTemplate();

        Handlebars.registerHelper(
            "toBadgeDescriptionWithBreaks",
            function (description) {
                const lines: Array<string> = [];
                let line = "";

                description = Handlebars.Utils.escapeExpression(description);
                _.each(description.split(" "), function (word) {
                    if (line.length > 0) {
                        // Split description into up to two lines

                        if (
                            line.length + word.length > 12 &&
                            lines.length === 0
                        ) {
                            // Insert newline, break it up
                            lines[lines.length] = line;
                            line = "";
                        } else {
                            line += " ";
                        }
                    }

                    line += word;
                });

                if (line) {
                    lines[lines.length] = line;
                }

                // Guarantee 2 lines for consistent height
                while (lines.length < 2) {
                    lines[lines.length] = "&nbsp;";
                }

                return lines.join("\n");
            },
        );
    },

    events: {
        "click .main-case .achievement-badge .delete-icon":
            "onDeleteBadgeClicked_",
        "click .main-case .achievement-badge": "onBadgeClicked_",
        "click .badge-picker .achievement-badge": "onBadgeInPickerClicked_",
        "click .display-case-cover": "onCoverClicked_",
    },

    /**
     * @return {boolean} Whether or not this display case can go into "edit" mode
     *        to allow a user to select which badges go inside.
     */
    isEditable: function (this: any) {
        return !!this.fullBadgeList;
    },

    /**
     * Sets the full badge list for the display case so it can go into edit
     * mode and pick badges from this badge list.
     * @param {Badges.UserBadgeList} The full list of badges that can be added
     *        to this display case.
     * @return {BadgeDisplayCase} This same instance so calls can be chained.
     */
    setFullBadgeList: function (this: any, fullBadgeList) {
        // TODO: do we want to listen to events on the full badge list?
        this.fullBadgeList = fullBadgeList;
        $(this.editControlEl).toggleClass("editable", this.isEditable());
    },

    /**
     * Enters "edit mode" where badges can be added/removed, if possible.
     * @param {number=} index Optional index of the slot in the display-case
     *        to be edited. Defaults to the first available slot, or if none
     *        are available, the last used slot.
     * @return {BadgeDisplayCase} This same instance so calls can be chained.
     */
    edit: function (this: any, index: number) {
        if (!this.isEditable() || this.editing) {
            return this;
        }

        this.setEditing_(true);

        this.updateEditSelection_(index);

        this.showBadgePicker_();
        this.editControlEl.slideUp(350);
        this.mainCaseEl.addClass("enable-scrolling");
        $(document).bind("mousedown", this.getBoundStopEditFn_());
        return this;
    },

    /**
     * Updates the editor so that the badge at the specified index is
     * being edited. If no index is specified, the last possible spot
     * is selected by default.
     * @param {number=} index Optional index of the slot in the display-case
     *        to be edited. -1 to indicate that none should be selected (i.e.
     *        we're exiting edit mode.
     */
    updateEditSelection_: function (this: any, index: number) {
        // By default, select the first empty slot, or the last non-empty
        // slot if completely full.
        if (index === undefined) {
            for (let i = 0, len = this.model.length; i < len; i++) {
                if (this.model.at(i).isEmpty()) {
                    index = i;
                    break;
                }
            }
        }
        index = index === undefined ? this.model.length : index;
        this.selectedIndex = Math.min(index, this.maxVisible - 1);
        this.updateSelectionHighlight();
    },

    /**
     * Shows the badge picker for edit mode, if not already visible.
     * This view must have already have been rendered once.
     */
    showBadgePicker_: function (this: any) {
        this.renderBadgePicker();
        const jel = $(this.el);
        const jelPicker = $(this.badgePickerEl);
        jelPicker
            .slideDown("fast", function () {
                jelPicker.show();
            })
            .css("margin-left", "300px")
            .animate(
                {"margin-left": "0"},
                {
                    duration: "fast",
                    step: $.easing.easeInOutCubic,
                    complete: function () {
                        jel.addClass("editing");
                    },
                },
            );

        return this;
    },

    /**
     * Handles a click to a badge in the main display case.
     */
    onBadgeClicked_: function (this: any, e) {
        if (!this.editing) {
            // Noop when not editing.
            return;
        }

        const index = $(this.mainCaseEl)
            .find(".achievement-badge")
            .index(e.currentTarget);
        this.updateEditSelection_(index);
        e.stopPropagation();
    },

    /**
     * Handles a click to a delete button for a badge in the main display case.
     */
    onDeleteBadgeClicked_: function (this: any, e) {
        // Prevent the badge click from being processed, since
        // the X is a child of the badge.
        e.stopPropagation();

        if (!this.editing) {
            // Noop when not editing.
            return;
        }

        let badgeNode = e.currentTarget;
        while (badgeNode && !$(badgeNode).hasClass("achievement-badge")) {
            badgeNode = badgeNode.parentNode;
        }
        const index = $(this.mainCaseEl)
            .find(".achievement-badge")
            .index(badgeNode);

        // Store position before it's removed.
        const fromOffset = $(badgeNode).offset();
        const isLast = index === this.model.length - 1;
        const removedBadge = this.model.at(index);
        this.model.remove(removedBadge);

        if (!isLast) {
            // Insert an empty badge, since we don't want things shifting
            this.model.add(Badges.Badge.EMPTY_BADGE.clone(), {at: index});
        }
        this.updateEditSelection_(index);

        // Animate-out the deleted badge
        this.ensureAnimatingBadgeEl();
        const badgeTemplate = getBadgeTemplate();
        this.animatingBadgeEl.html(
            badgeTemplate(removedBadge.toJSON(), {
                helpers: {
                    staticUrl,
                },
            }),
        );
        this.animatingBadgeEl.css({
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            left: fromOffset.left,
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            top: fromOffset.top,
            opacity: 1.0,
        });
        this.animatingBadgeEl.show();

        this.animatingBadgeEl.animate(
            {
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                left: fromOffset.left + 5,
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                top: fromOffset.top + 10,
                opacity: 0,
            },
            {
                duration: 250,
                step: $.easing.easeInOutCubic,
                complete: _.bind(function (this: any) {
                    this.animatingBadgeEl.hide();
                    this.animatingBadgeEl.css({opacity: 1.0});
                }, this),
            },
        );
    },

    /**
     * Handles a click to a badge in the badge picker in edit mode.
     */
    onBadgeInPickerClicked_: function (this: any, e) {
        e.stopPropagation();

        if ($(e.currentTarget).hasClass("used")) {
            // Ignore badges already in the main case.
            return;
        }

        const name = e.currentTarget.id;
        const matchedBadge = _.find(
            this.fullBadgeList.models,
            function (userBadge) {
                return userBadge.get("badge").get("name") === name;
            },
        );
        if (!matchedBadge) {
            // Shouldn't happen!
            return;
        }

        const badgeModel = matchedBadge.get("badge");
        if (!badgeModel) {
            return;
        }
        const slug = badgeModel.get("slug");
        const models = this.model && this.model.models;
        const existingSlugs =
            (models &&
                models.map &&
                models.map((badge) => badge.get("slug"))) ||
            [];

        if (existingSlugs.includes(slug)) {
            // This badge has already been added
            return;
        }

        const badgeToAdd = matchedBadge.get("badge").clone();
        this.beginSelectionAnimation_(
            badgeToAdd,
            $(e.currentTarget),
            this.selectedIndex,
        );
    },

    ensureAnimatingBadgeEl: function (this: any) {
        if (!this.animatingBadgeEl) {
            this.animatingBadgeEl = $(
                "<div id='animating-badge'></div>",
            ).appendTo("body");
        }
    },

    /**
     * Begin an animation to select a badge from the picker so that
     * it may be added to the main display case.
     *
     * @param {Badges.Badge} badgeSelected The badge to add
     * @param {jQuery} jelBadgeSelected The jQuery element of the badge
     *     element that was selected in the picker.
     * @param {number} index The slot in the display case to add to.
     */
    beginSelectionAnimation_: function (
        this: any,
        badgeSelected,
        jelBadgeSelected,
        index,
    ) {
        this.ensureAnimatingBadgeEl();

        const jelTargetSlot = $(this.mainCaseEl)
            .find(".achievement-badge")
            .eq(index);
        const badgeTemplate = getBadgeTemplate();
        this.animatingBadgeEl.html(badgeTemplate(badgeSelected.toJSON()));

        const fromOffset = jelBadgeSelected.offset();
        this.animatingBadgeEl.css({
            left: fromOffset.left,
            top: fromOffset.top,
        });
        this.animatingBadgeEl.show();

        const toOffset = jelTargetSlot.offset();
        this.animatingBadgeEl.animate(
            {
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                left: toOffset.left,
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                top: toOffset.top,
            },
            {
                duration: 250,
                step: $.easing.easeInOutCubic,
                complete: _.bind(function (this: any) {
                    this.finishSelection_(badgeSelected, index);
                }, this),
            },
        );
    },

    finishSelection_: function (this: any, badgeToAdd, index) {
        if (!this.animatingBadgeEl) {
            return;
        }
        this.animatingBadgeEl.hide();
        this.animatingBadgeEl.html("");

        // Do the actual selection!
        // Backbone.Collection doesn't have a .replace method - do it ourselves
        const existing = this.model.at(index);
        if (existing) {
            this.model.remove(existing);
        }

        for (let i: number = this.model.length; i < index; i++) {
            // Ensure we pad the list with empty badges if the user is
            // inserting after some holes.
            this.model.add(Badges.Badge.EMPTY_BADGE.clone());
        }
        this.model.add(badgeToAdd, {at: index});

        // Pick the next empty slot.
        this.updateEditSelection_();
    },

    /**
     * Exits edit mode.
     */
    stopEdit: function (this: any) {
        if (this.editing) {
            this.setEditing_(false);
            this.updateEditSelection_(-1);
            const jelRootEl = $(this.el);
            const jelPicker = $(this.badgePickerEl);
            jelPicker.slideUp("fast", function () {
                jelRootEl.removeClass("editing");
            });
            jelPicker.undelegate();
            this.editControlEl.slideDown(250);
            this.mainCaseEl.removeClass("enable-scrolling");
            $(document).unbind("click", this.getBoundStopEditFn_());

            // TODO: avoid saving if not dirty.
            this.save();
        }
        return this;
    },

    getBoundStopEditFn_: function (this: any) {
        if (this.boundStopEditFn_) {
            return this.boundStopEditFn_;
        }
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        this.boundStopEditFn_ = function (e: unknown) {
            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            for (let node = e.target; node; node = node.parentNode) {
                if (node === self.el) {
                    // Click inside the display-case somewhere - ignore.
                    return;
                }
            }
            self.stopEdit();
        };
        return this.boundStopEditFn_;
    },

    save: function (this: any) {
        this.model.save();
    },

    setEditing_: function (this: any, editing) {
        this.editing = editing;
    },

    /**
     * Builds a context object to render a single badge.
     */
    getUserBadgeJsonContext_: function (badge) {
        const json = badge.get("badge").toJSON();
        json["count"] = badge.get("count");
        return json;
    },

    /**
     * Renders the contents of the main case.
     */
    renderMainCaseContents_: function (this: any) {
        let i;
        const html: Array<string> = [];
        const numRendered = Math.min(this.maxVisible, this.model.length);
        const badgeTemplate = getBadgeTemplate();

        // While creating the JSON context, also update the badge overlays in
        // the display case cover.
        const overlays = this.editControlEl.find(".achievement-badge");
        for (i = 0; i < numRendered; i++) {
            const badge = this.model.at(i);
            html.push(badgeTemplate(badge.toJSON()));
            overlays[i].setAttribute(
                "title",
                badge.get("translatedExtendedDescription"),
            );
        }
        for (; i < this.maxVisible; i++) {
            html.push(badgeTemplate(Badges.Badge.EMPTY_BADGE.toJSON()));
            overlays[i].setAttribute("title", "");
        }
        this.mainCaseEl.html(html.join(""));
    },

    /**
     * Updates the appropriate badge being highlighted for edit mode.
     * See {@link #selectedIndex} for more details.
     */
    updateSelectionHighlight: function (this: any) {
        const badgeSlots = $(".achievement-badge", this.mainCaseEl);
        badgeSlots.removeClass("selected");
        if (this.selectedIndex > -1) {
            $(badgeSlots[this.selectedIndex]).addClass("selected");
        }
    },

    onCoverClicked_: function (this: any, e) {
        if (this.isEditable()) {
            this.edit();
        }
        e.stopPropagation();
    },

    /**
     * Renders the contents of the badge picker.
     * Idempotent - simply blows away and repopulates the contents if called
     * multiple times.
     */
    renderBadgePicker: function (this: any) {
        if (this.fullBadgeList.isEmpty()) {
            const emptyBadgePickerTemplate = getEmptyBadgePickerTemplate();
            $(this.badgePickerEl).html(emptyBadgePickerTemplate());
            return;
        }

        const html: Array<string> = [];
        const badgeTemplate = getBadgeTemplate();
        this.fullBadgeList.each(function (this: any, userBadge) {
            const alreadyInCase = this.model.find(function (b) {
                return b.get("name") === userBadge.get("badge").get("name");
            });

            // Mark badges that are already used in the display case
            const jsonContext = this.getUserBadgeJsonContext_(userBadge);
            if (alreadyInCase) {
                jsonContext["used"] = true;
            }
            html.push(badgeTemplate(jsonContext));
        }, this);
        $(this.badgePickerEl).html(html.join(""));
    },

    render: function (this: any) {
        if (!this.mainCaseEl) {
            // First render - build the chrome.
            const badgeDisplayCaseTemplate = getBadgeDisplayCaseTemplate();
            $(this.el).html(badgeDisplayCaseTemplate());
            this.mainCaseEl = this.$(".main-case");
            this.badgePickerEl = this.$(".badge-picker");
            this.editControlEl = this.$(".display-case-cover");

            $(this.editControlEl).toggleClass("editable", this.isEditable());
        }
        this.renderMainCaseContents_();
        if (this.fullBadgeList) {
            this.renderBadgePicker();
        }
        this.updateSelectionHighlight();
        return this;
    },
});

export default BadgeDisplayCase;
